import { v4 as uuid } from "uuid";
import { format, formatISO, parseISO, startOfDay } from "date-fns";
import type {
  InputFormat,
  ParticipationQuestionnaire,
  Question,
  QuestionnaireProposal,
  QuestionType,
  Translations,
} from "../types";
import { proposalTimes, type ProposalFields } from "../proposalTimes";

// `key` is the stable client key, `id` the server id of a stored question, option or proposal
export type EditorOption = {
  key: string;
  id?: string;
  label: string;
  translations?: Translations;
};

export type EditorProposal = ProposalFields & { key: string; id?: string };

export type EditorQuestion = {
  key: string;
  id?: string;
  type: QuestionType;
  content: string;
  required: boolean;
  input_format: InputFormat;
  multiple_select: boolean;
  option_adding: boolean;
  add_improper_option: boolean;
  options: EditorOption[];
  proposals: EditorProposal[];
  translations?: Translations;
};

export type EditorValues = {
  title: string;
  translations?: Translations;
  description: string;
  anonymous: boolean;
  ends_at: string | null;
  questions: EditorQuestion[];
};

export const IMPROPER_SUFFIX = "-none";

export function newOption(label = ""): EditorOption {
  return { key: uuid(), label };
}

export function newProposal(
  date: string | null = null,
  time: string | null = null,
  time_end: string | null = null,
): EditorProposal {
  return { key: uuid(), date, time, time_end };
}

function fromProposal(proposal: QuestionnaireProposal): EditorProposal {
  const start = proposal.starts_at ? parseISO(proposal.starts_at) : null;

  return {
    key: proposal.id,
    id: proposal.id,
    date: start && formatISO(startOfDay(start)),
    time: start && format(start, "HH:mm"),
    time_end: proposal.ends_at && format(parseISO(proposal.ends_at), "HH:mm"),
  };
}

export function newQuestion(type: QuestionType = "text"): EditorQuestion {
  return {
    key: uuid(),
    type,
    content: "",
    required: false,
    input_format: "text",
    multiple_select: false,
    option_adding: false,
    add_improper_option: false,
    // two to start with, like the composer's survey and schedule
    options: type === "poll" ? [newOption(), newOption()] : [],
    proposals: type === "schedule" ? [newProposal(), newProposal()] : [],
  };
}

// The text, its languages and "required" survive a type change, the rest starts fresh
export function changeType(
  question: EditorQuestion,
  type: QuestionType,
): EditorQuestion {
  return {
    ...newQuestion(type),
    key: question.key,
    content: question.content,
    translations: question.translations,
    required: question.required,
  };
}

// A copy is a new question: fresh keys and no server ids
export function duplicateQuestion(question: EditorQuestion): EditorQuestion {
  return {
    ...question,
    key: uuid(),
    id: undefined,
    options: question.options.map((option) => ({
      ...newOption(option.label),
      translations: option.translations,
    })),
    proposals: question.proposals.map((proposal) =>
      newProposal(proposal.date, proposal.time, proposal.time_end),
    ),
  };
}

export function fromQuestion(question: Question): EditorQuestion {
  return {
    ...newQuestion(question.type),
    key: question.id,
    id: question.id,
    content: question.content,
    // without this the next save would overwrite the stored languages with {}
    translations: question.translations || {},
    required: question.required,
    input_format: question.input_format || "text",
    multiple_select: !!question.multiple_select,
    option_adding: !!question.option_adding,
    add_improper_option: !!question.add_improper_option,
    options: (question.options || []).map((option) => ({
      key: option.id,
      id: option.id,
      label: option.label,
      translations: option.translations || {},
    })),
    proposals: (question.proposals || [])
      .filter((proposal) => !proposal.improper)
      .map(fromProposal),
  };
}

export function toQuestionParams(question: EditorQuestion) {
  const base = {
    id: question.id,
    type: question.type,
    content: question.content.trim(),
    required: question.required,
    translations: question.translations || {},
  };

  switch (question.type) {
    case "text":
      return { ...base, input_format: question.input_format };
    case "poll":
      return {
        ...base,
        multiple_select: question.multiple_select,
        option_adding: question.option_adding,
        options: question.options.map(({ id, label, translations }) => ({
          id,
          label,
          translations: translations || {},
        })),
      };
    case "schedule":
      return {
        ...base,
        multiple_select: question.multiple_select,
        option_adding: question.option_adding,
        add_improper_option: question.add_improper_option,
        proposals: question.proposals.map((proposal) => ({
          id: proposal.id,
          ...proposalTimes(proposal),
        })),
      };
  }
}

export function toPayload(values: EditorValues) {
  return {
    title: values.title.trim(),
    translations: values.translations || {},
    description: values.description,
    anonymous: values.anonymous,
    ends_at: values.ends_at || null,
    questions: values.questions.map(toQuestionParams),
  };
}

// The participants' view with keys as ids, without empty options and dates, like the server
export function toPreview(values: EditorValues): ParticipationQuestionnaire {
  return {
    id: "preview",
    title: values.title,
    description: values.description,
    anonymous: values.anonymous,
    ends_at: values.ends_at || null,
    closed: false,
    my_submission: null,
    questions: values.questions.map((question) => ({
      id: question.key,
      type: question.type,
      content: question.content,
      required: question.required,
      input_format: question.input_format,
      multiple_select: question.multiple_select,
      option_adding: question.option_adding,
      add_improper_option: question.add_improper_option,
      options: question.options
        .filter((option) => option.label.trim() !== "")
        .map((option) => ({ id: option.key, label: option.label })),
      proposals: [
        ...question.proposals
          .filter((proposal) => proposal.date)
          .map((proposal) => ({
            id: proposal.key,
            ...proposalTimes(proposal),
            improper: false,
          })),
        ...(question.add_improper_option
          ? [
              {
                id: `${question.key}${IMPROPER_SUFFIX}`,
                starts_at: null,
                ends_at: null,
                improper: true,
              },
            ]
          : []),
      ],
    })),
  };
}
